/**
 * Date formatting, one shape for the whole storefront (BUG-17: three date formats on
 * adjacent screens — "Aug 03, 2026" on a sale, "Sep 1" on the rental feed, "01 Sep" in
 * the booking summary).
 *
 * The canon is day-before-month with the year written out: "12 Sep 2026". Day-first is
 * unambiguous to every reader where "09/12" and "12/09" are the same string with two
 * meanings, and the shape never varies with the browser locale — same reason money is
 * pinned in `money.ts` ([[tolocalestring-follows-browser-locale]]).
 *
 * Parsing is dayjs on purpose: a date-only string ("2026-09-15") must stay the calendar
 * day it names in every timezone, and dayjs reads it as *local* midnight, while
 * `new Date('2026-09-15')` is UTC midnight and reads as the previous day west of
 * Greenwich. For a calendar day carried inside an ISO *moment* (the tracker's deal
 * steps), use `formatUtcDay` — those days are defined in UTC, not on the reader's clock.
 */

import type { Dayjs } from 'dayjs';

import dayjs from 'dayjs';

type DateInput = string | number | Date | Dayjs;

const DAY_FORMAT = 'D MMM YYYY';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatWith = (value: DateInput | null | undefined, pattern: string): string => {
  if (value == null || value === '') {
    return '—';
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format(pattern) : String(value);
};

/** "12 Sep 2026" — a calendar day, read on the visitor's own clock. */
export const formatDay = (value: DateInput | null | undefined): string =>
  formatWith(value, DAY_FORMAT);

/** "12 Sep 2026, 14:32" — a moment; the time is the visitor's local time. */
export const formatDateTime = (value: DateInput | null | undefined): string =>
  formatWith(value, `${DAY_FORMAT}, HH:mm`);

/**
 * "12 Sep 2026" — the calendar day of an ISO moment, in UTC. For server-defined days
 * (estimated delivery, deal steps): those are days, not moments, and must not shift a
 * day backwards or forwards with the reader's timezone.
 */
export const formatUtcDay = (iso: string | Date): string => {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return String(iso);
  }

  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
};
